import {
	Button,
	ButtonProps as ButtonPropsFromMui,
	ButtonGroup,
	ButtonGroupProps,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuItemProps,
	MenuList,
	Paper,
	Popper,
	PopperProps,
} from '@mui/material';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import styles from './ButtonGroupButton.module.scss';

type OnClickHandler = React.MouseEventHandler<
HTMLButtonElement | HTMLLIElement | HTMLDivElement
>;

export type ButtonGroupButtonProps = {
	optionsLabel: string;
	options: MenuItemProps[];
	buttonProps?: Omit<
	ButtonPropsFromMui,
	'variant' | 'startIcon' | 'loading' | 'onClick'
	> & { onClick?: OnClickHandler };
	children?: React.ReactNode;
	'data-cy'?: string;
} & Omit<ButtonGroupProps, 'ref' | 'onClick'> &
Pick<ButtonPropsFromMui, 'startIcon' | 'loading'> &
Pick<PopperProps, 'placement'>;

export const ButtonGroupButton: React.FC<ButtonGroupButtonProps> = ( {
	optionsLabel,
	options,
	buttonProps,
	startIcon,
	loading,
	children,
	placement,
	'data-cy': dataCy,
	...buttonGroupProps
} ) => {
	const [ open, setOpen ] = useState( false );
	const anchorRef = useRef<HTMLDivElement>( null );

	const { onClick: buttonPropsOnClick, ...otherButtonProps } =
		buttonProps || {};

	const handleToggle: OnClickHandler = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen( ( prevOpen ) => !prevOpen );
	};

	const handleDefaultClick: OnClickHandler = ( e ) => {
		e.preventDefault();
		e.stopPropagation();
		if ( buttonPropsOnClick ) {
			buttonPropsOnClick( e );
		} else {
			handleToggle( e );
		}
	};

	const handleClose = ( event: Event ) => {
		if ( anchorRef.current?.contains( event.target as HTMLElement ) ) {
			return;
		}
		setOpen( false );
	};

	return (
		<>
			<ButtonGroup
				{ ...{
					...buttonGroupProps,
					...( dataCy ? { 'data-cy': `${ dataCy }-container` } : {} ),
				} }
				variant={ buttonGroupProps.variant || 'contained' }
				ref={ anchorRef }
				className={ classNames( buttonGroupProps.className, styles.container ) }
				disableElevation
			>
				<Button
					{ ...{
						...otherButtonProps,
						...( dataCy ? { 'data-cy': dataCy } : {} ),
					} }
					onClick={ handleDefaultClick }
					startIcon={ startIcon }
					loading={ loading }
					variant={ buttonGroupProps.variant || 'contained' }
					className={ classNames( buttonProps?.className, styles.button ) }
				>
					{ children }
				</Button>
				<Button
					className={ styles.optionsButton }
					aria-controls={ open ? 'split-button-menu' : undefined }
					aria-expanded={ open ? 'true' : undefined }
					aria-label={ optionsLabel }
					aria-haspopup='menu'
					onClick={ handleToggle }
					disabled={ !!buttonGroupProps.disabled || !!loading }
					{ ...{
						...( dataCy ? { 'data-cy': `${ dataCy }-toggle-menu` } : {} ),
					} }
				>
					<span
						className={ classNames( styles.icon, open ? styles.open : undefined ) }
					/>
				</Button>
			</ButtonGroup>
			<Popper
				open={ open }
				anchorEl={ anchorRef.current }
				role={ undefined }
				transition
				disablePortal
				placement={ placement || 'top-end' }
			>
				{ ( { TransitionProps, placement } ) => (
					<Grow
						{ ...TransitionProps }
						style={ {
							transformOrigin:
								placement === 'bottom' ? 'center top' : 'center bottom',
						} }
					>
						<Paper>
							<ClickAwayListener onClickAway={ handleClose }>
								<MenuList
									id='split-button-menu'
									autoFocusItem
									{ ...{ ...( dataCy ? { 'data-cy': `${ dataCy }-menu` } : {} ) } }
								>
									{ options.map( ( option, index ) => (
										<MenuItem
											key={ index }
											{ ...option }
											onClick={ ( e ) => {
												handleToggle( e );
												option.onClick?.( e );
											} }
										/>
									) ) }
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				) }
			</Popper>
		</>
	);
};
